import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { Tabs, TabPanel } from '@/components/ui/Tabs';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { FormSelect } from '@/components/forms/FormField';
import {
  useContact,
  useCreateContact,
  useUpdateContact,
  useTags,
  useContactActivity,
  checkContactDuplicates,
  type CreateContactPayload,
  type DuplicateMatch,
  type Tag,
} from './hooks/useContacts';
import { DuplicateWarningModal } from './DuplicateWarningModal';
import { useContactNotes, useCreateNote } from './hooks/useNotesReminders';
import { useNoteCategories } from './hooks/useNoteCategories';
import { useToast } from '@/components/ui/Toast';
import { useEntityTypes } from '@/features/settings/hooks/useEntityTypes';
import { useResolveFormForEntityType } from '@/features/form-builder/hooks/useFormBuilder';
import {
  DynamicFormRenderer,
  validateFieldPattern,
} from '@/features/form-builder/DynamicFormRenderer';

/**
 * ContactForm — create or edit a contact.
 * Uses the dynamic form renderer based on the selected entity type's form definition.
 */
export function ContactForm() {
  const { contactId: id } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const { data: existingContact, isLoading: contactLoading } = useContact(id ?? '');
  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact(id ?? '');
  const { data: entityTypes } = useEntityTypes();
  const { data: availableTags } = useTags();

  const [selectedEntityTypeId, setSelectedEntityTypeId] = useState('');
  const { data: formDefinition, isLoading: formLoading } =
    useResolveFormForEntityType(selectedEntityTypeId);

  const [activeTab, setActiveTab] = useState('details');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // Duplicate-detection confirm flow (create only). When an email/phone matches
  // an existing contact, we hold the prepared payload and show a warning modal
  // instead of creating immediately.
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>([]);
  const [pendingPayload, setPendingPayload] = useState<CreateContactPayload | null>(null);

  // Tracks which contact id has already had its saved values hydrated into the
  // form. The edit-populate effect must run only ONCE per loaded contact —
  // otherwise it re-fires whenever `formDefinition` changes (e.g. the user
  // switches the entity type), which would reset the entity type back to the
  // original and wipe any in-progress edits.
  const hydratedContactIdRef = useRef<string | null>(null);

  const methods = useForm({
    // Validate on blur so field formats (regex) surface inline, not only on save.
    mode: 'onBlur',
    defaultValues: {
      entityTypeId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      secondaryPhone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Canada',
      dateOfBirth: '',
      businessName: '',
      businessNumber: '',
      clientCode: '',
      mailingName: '',
      salutation: '',
      deliveryMethod: '',
      contactType: '',
      leadSource: '',
      customFields: {} as Record<string, unknown>,
    } as Record<string, unknown>,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;

  // Sync entity type selection with form state
  const watchedEntityTypeId = watch('entityTypeId') as string;
  useEffect(() => {
    if (watchedEntityTypeId && watchedEntityTypeId !== selectedEntityTypeId) {
      setSelectedEntityTypeId(watchedEntityTypeId);
    }
  }, [watchedEntityTypeId, selectedEntityTypeId]);

  // Notify user when no form is attached to selected entity type
  useEffect(() => {
    if (selectedEntityTypeId && !formLoading && !formDefinition) {
      toast({
        variant: 'warning',
        title: 'No form attached to this entity type',
        description: 'Please configure a form in Settings → Form Builder.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityTypeId, formLoading, formDefinition]);

  // Apply default values from form definition when creating a new record
  useEffect(() => {
    if (isEditing || !formDefinition?.sections) return;

    const camelCase = (str: string) =>
      str.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());

    for (const section of formDefinition.sections) {
      for (const field of section.fields) {
        if (!field.is_active || !field.default_value) continue;

        const fieldName =
          field.is_system_field && field.db_column
            ? camelCase(field.db_column)
            : `customFields.${field.field_key}`;

        // Only set if the current value is empty (don't overwrite user input)
        const currentValue = methods.getValues(fieldName as never) as unknown;
        if (currentValue === '' || currentValue === undefined || currentValue === null) {
          methods.setValue(fieldName as never, field.default_value as never);
        }
      }
    }
  }, [formDefinition, isEditing, methods]);

  // Populate form when editing — ONCE per loaded contact. Guarded by
  // hydratedContactIdRef so switching the entity type (which changes
  // formDefinition and re-triggers this effect) does not revert the selection
  // or clobber edits in progress.
  useEffect(() => {
    if (!existingContact || !isEditing) return;
    if (hydratedContactIdRef.current === existingContact.id) return;

    const contactEntityTypeId = existingContact.entityTypeId ?? '';

    // Phase 1: as soon as the contact loads, point the form-definition fetch at
    // the contact's OWN entity type. This must happen before we can map
    // entity-type-specific fields, because `formDefinition` is resolved from
    // `selectedEntityTypeId`. We do NOT latch the ref here, so hydration will
    // re-run once the definition resolves.
    if (selectedEntityTypeId !== contactEntityTypeId) {
      setSelectedEntityTypeId(contactEntityTypeId);
      return;
    }

    // Phase 2: wait for the form definition of THIS entity type to resolve so
    // system/custom fields map correctly. If the entity type genuinely has no
    // form (fetch finished, definition still null), fall through and hydrate
    // the known fields so the form is still usable. Latch only once we get
    // here — i.e. once the field loop has had the real definition available.
    const hasEntityType = !!contactEntityTypeId;
    const definitionPending = hasEntityType && !formDefinition && formLoading;
    if (definitionPending) return;

    const values: Record<string, unknown> = {
      entityTypeId: contactEntityTypeId,
      customFields: {},
    };

    // Load ALL field values from form_data (the single source of truth)
    const contactData = existingContact as unknown as Record<string, unknown>;
    const formDataRaw = contactData.formData || contactData.form_data;
    const savedFormData =
      typeof formDataRaw === 'string'
        ? JSON.parse(formDataRaw)
        : ((formDataRaw as Record<string, unknown> | undefined) ?? {});

    const camelCase = (str: string) =>
      str.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());

    // Coerce a stored value into the shape a given field type expects, so every
    // field type hydrates reliably in edit mode (checkbox→boolean,
    // multi_select→string[], table→row[], others→as-is).
    const coerceByType = (fieldType: string, raw: unknown): unknown => {
      if (fieldType === 'checkbox') {
        if (typeof raw === 'boolean') return raw;
        if (typeof raw === 'string') return raw === 'true' || raw === '1';
        return !!raw;
      }
      if (fieldType === 'multi_select') {
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [raw];
          } catch {
            // Comma-separated fallback
            return raw.includes(',') ? raw.split(',').map((s) => s.trim()) : [raw];
          }
        }
        return [];
      }
      if (fieldType === 'table') {
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      }
      return raw ?? '';
    };

    const customFields: Record<string, unknown> = {};

    // Populate form from form_data: system fields as top-level, custom as customFields
    if (formDefinition?.sections) {
      for (const section of formDefinition.sections) {
        for (const field of section.fields) {
          if (!field.is_active) continue;
          if (field.is_system_field && field.db_column) {
            const camelKey = camelCase(field.db_column);
            // Try form_data first (snake_case key), then contact object (camelCase)
            const val =
              savedFormData[field.db_column] ??
              savedFormData[field.field_key] ??
              contactData[camelKey];
            values[camelKey] = coerceByType(field.field_type, val);
          } else {
            // Custom field — store under customFields, coerced to the field's shape
            const val = savedFormData[field.field_key];
            customFields[field.field_key] = coerceByType(field.field_type, val);
          }
        }
      }
    }

    // Map custom field_key → field_type (for coercing EAV/legacy values).
    const keyToType = new Map<string, string>();
    const labelToKey = new Map<string, string>();
    if (formDefinition?.sections) {
      for (const section of formDefinition.sections) {
        for (const field of section.fields) {
          if (!field.is_system_field) {
            keyToType.set(field.field_key, field.field_type);
            labelToKey.set(field.label, field.field_key);
          }
        }
      }
    }

    // Overlay custom values from the EAV stores on top of anything read from
    // form_data (merge, do NOT replace — form_data may hold values the EAV
    // rows don't, and vice-versa). Prefer non-empty EAV values.
    if (existingContact.formFieldValues?.length) {
      for (const fv of existingContact.formFieldValues) {
        if (fv.value !== undefined && fv.value !== null && fv.value !== '') {
          customFields[fv.fieldKey] = coerceByType(keyToType.get(fv.fieldKey) ?? 'text', fv.value);
        } else if (!(fv.fieldKey in customFields)) {
          customFields[fv.fieldKey] = coerceByType(keyToType.get(fv.fieldKey) ?? 'text', '');
        }
      }
    }
    // Legacy dynamic fields: match by field_key resolved from the form
    // definition (NOT the field-definition UUID, which the renderer never
    // reads). Only fill keys not already set from the sources above.
    if (existingContact.dynamicFields?.length && labelToKey.size) {
      for (const df of existingContact.dynamicFields) {
        const key = labelToKey.get(df.fieldLabel);
        if (key && (customFields[key] === undefined || customFields[key] === '')) {
          customFields[key] = coerceByType(keyToType.get(key) ?? 'text', df.value);
        }
      }
    }

    values.customFields = customFields;

    // Fallback: ensure basic fields are always populated even when no form
    // definition drove them above.
    if (!values.firstName) values.firstName = existingContact.firstName ?? '';
    if (!values.lastName) values.lastName = existingContact.lastName ?? '';
    if (!values.email) values.email = existingContact.email ?? '';

    reset(values);

    if (existingContact.tags?.length) {
      setSelectedTagIds(existingContact.tags.map((t) => t.id));
    }

    // Latch: hydration is complete for this contact with its real form
    // definition. The effect won't re-run (so switching entity type later won't
    // revert the selection or clobber edits).
    hydratedContactIdRef.current = existingContact.id;
  }, [existingContact, isEditing, reset, formDefinition, formLoading, selectedEntityTypeId]);

  // Performs the actual create request and post-create navigation. Separated
  // from onSubmit so it can be invoked either directly (no duplicates) or after
  // the user confirms the duplicate warning.
  const performCreate = async (payload: CreateContactPayload) => {
    try {
      await createMutation.mutateAsync(payload);
      toast({ variant: 'success', title: 'Contact created successfully' });
      navigate('/contacts');
    } catch {
      toast({ variant: 'error', title: 'Failed to create contact' });
    }
  };

  const handleConfirmDuplicate = async () => {
    if (!pendingPayload) return;
    const payload = pendingPayload;
    await performCreate(payload);
    setPendingPayload(null);
    setDuplicateMatches([]);
  };

  const handleCancelDuplicate = () => {
    setPendingPayload(null);
    setDuplicateMatches([]);
  };

  const onSubmit = async (values: Record<string, unknown>) => {
    // Validate required fields from form definition
    if (formDefinition?.sections) {
      const missingFields: string[] = [];
      const formatErrors: string[] = [];
      for (const section of formDefinition.sections) {
        for (const field of section.fields) {
          if (!field.is_active) continue;

          let fieldValue: unknown;
          if (field.is_system_field && field.db_column) {
            const camelKey = field.db_column.replace(/_([a-z])/g, (_: string, c: string) =>
              c.toUpperCase(),
            );
            fieldValue = methods.getValues(camelKey as never);
          } else {
            fieldValue = methods.getValues(`customFields.${field.field_key}` as never);
            // Fallback: check customFields object directly
            if (fieldValue === undefined) {
              const allCustom = methods.getValues('customFields' as never) as
                | Record<string, unknown>
                | undefined;
              if (allCustom) {
                fieldValue = allCustom[field.field_key];
              }
            }
          }

          // Check if the field itself is required and empty
          if (field.is_required) {
            if (
              fieldValue === undefined ||
              fieldValue === null ||
              fieldValue === '' ||
              (Array.isArray(fieldValue) && fieldValue.length === 0)
            ) {
              missingFields.push(field.label);
              continue;
            }
          }

          // Regex format enforcement (validation_rules.pattern) for text-like
          // fields. Empty values are allowed here (required is handled above).
          const patternError = validateFieldPattern(field, fieldValue);
          if (patternError) {
            formatErrors.push(patternError);
            continue;
          }

          // Table field column validation — only validates columns visible on the form
          if (field.field_type === 'table') {
            let vRules: Record<string, unknown> = {};
            if (typeof field.validation_rules === 'string') {
              try {
                vRules = JSON.parse(field.validation_rules);
              } catch {
                /* ignore */
              }
            } else if (field.validation_rules && typeof field.validation_rules === 'object') {
              vRules = field.validation_rules as Record<string, unknown>;
            }

            // Only validate columns that are actually rendered (present in field.options)
            const visibleColumnKeys = new Set((field.options || []).map((o) => o.value));

            const colRequired = vRules.column_required as Record<string, boolean> | undefined;
            const colUnique = vRules.column_unique as Record<string, boolean> | undefined;
            const hasRequiredColumns =
              colRequired &&
              Object.entries(colRequired).some(([key, val]) => val && visibleColumnKeys.has(key));

            // If any visible column is required but table has no rows, require at least one row
            if (hasRequiredColumns && (!Array.isArray(fieldValue) || fieldValue.length === 0)) {
              missingFields.push(`${field.label} (at least one row required)`);
            }

            if (Array.isArray(fieldValue) && fieldValue.length > 0) {
              if (colRequired) {
                for (const [colKey, isReq] of Object.entries(colRequired)) {
                  if (!isReq) continue;
                  // Skip columns that are not visible on the form
                  if (!visibleColumnKeys.has(colKey)) continue;
                  const emptyRows = (fieldValue as Record<string, string>[]).filter(
                    (row) => !row[colKey],
                  );
                  if (emptyRows.length > 0) {
                    const colLabel =
                      field.options?.find((o) => o.value === colKey)?.label || colKey;
                    missingFields.push(
                      `${field.label} → ${colLabel} (row has empty required value)`,
                    );
                    break;
                  }
                }
              }

              if (colUnique) {
                for (const [colKey, isUniq] of Object.entries(colUnique)) {
                  if (!isUniq) continue;
                  // Skip columns that are not visible on the form
                  if (!visibleColumnKeys.has(colKey)) continue;
                  const values = (fieldValue as Record<string, string>[])
                    .map((row) => row[colKey])
                    .filter(Boolean);
                  const hasDuplicates = values.length !== new Set(values).size;
                  if (hasDuplicates) {
                    const colLabel =
                      field.options?.find((o) => o.value === colKey)?.label || colKey;
                    missingFields.push(`${field.label} → ${colLabel} (has duplicate values)`);
                    break;
                  }
                }
              }
            }
          }
        }
      }

      if (missingFields.length > 0) {
        toast({
          variant: 'error',
          title: 'Required fields missing',
          description: `Please fill in: ${missingFields.join(', ')}`,
        });
        return;
      }

      if (formatErrors.length > 0) {
        toast({
          variant: 'error',
          title: 'Invalid format',
          description: formatErrors.join(', '),
        });
        return;
      }
    }

    const { customFields, entityTypeId, ...systemFields } = values;

    const payload: CreateContactPayload = {
      entityTypeId: entityTypeId as string,
      firstName: (systemFields.firstName as string) ?? '',
      lastName: (systemFields.lastName as string) ?? '',
      email: (systemFields.email as string) || undefined,
      phone: (systemFields.phone as string) || undefined,
      secondaryPhone: (systemFields.secondaryPhone as string) || undefined,
      addressLine1: (systemFields.addressLine1 as string) || undefined,
      addressLine2: (systemFields.addressLine2 as string) || undefined,
      city: (systemFields.city as string) || undefined,
      province: (systemFields.province as string) || undefined,
      postalCode: (systemFields.postalCode as string) || undefined,
      country: (systemFields.country as string) || undefined,
      dateOfBirth: (systemFields.dateOfBirth as string) || undefined,
      businessName: (systemFields.businessName as string) || undefined,
      businessNumber: (systemFields.businessNumber as string) || undefined,
      clientCode: (systemFields.clientCode as string) || undefined,
      mailingName: (systemFields.mailingName as string) || undefined,
      salutation: (systemFields.salutation as string) || undefined,
      deliveryMethod: (systemFields.deliveryMethod as string) || undefined,
      contactType: (systemFields.contactType as string) || undefined,
      leadSource: (systemFields.leadSource as string) || undefined,
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    };

    // Collect all remaining system fields not explicitly mapped above
    // These will be sent as dynamic_fields — the API extracts system fields
    // (those with db_column) and saves them to the contacts table directly.
    const MAPPED_FIELDS = new Set([
      'firstName',
      'lastName',
      'email',
      'phone',
      'secondaryPhone',
      'addressLine1',
      'addressLine2',
      'city',
      'province',
      'postalCode',
      'country',
      'dateOfBirth',
      'businessName',
      'businessNumber',
      'clientCode',
      'mailingName',
      'salutation',
      'deliveryMethod',
      'contactType',
      'leadSource',
    ]);

    const extraFields: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(systemFields)) {
      if (!MAPPED_FIELDS.has(key) && value !== undefined && value !== '') {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        extraFields[snakeKey] = value;
      }
    }

    // Merge custom fields + extra system fields into dynamicFields
    const allDynamicFields: Record<string, unknown> = {
      ...(customFields && typeof customFields === 'object'
        ? (customFields as Record<string, unknown>)
        : {}),
      ...extraFields,
    };

    if (Object.keys(allDynamicFields).length > 0) {
      payload.dynamicFields = allDynamicFields;
    }

    // Save ALL form values as form_data JSONB (the single source of truth for dynamic forms)
    const formData: Record<string, unknown> = { ...extraFields };
    // Add explicitly mapped system fields
    if (payload.firstName) formData.first_name = payload.firstName;
    if (payload.lastName) formData.last_name = payload.lastName;
    if (payload.email) formData.email = payload.email;
    if (payload.phone) formData.phone = payload.phone;
    if (payload.secondaryPhone) formData.secondary_phone = payload.secondaryPhone;
    if (payload.addressLine1) formData.address_line1 = payload.addressLine1;
    if (payload.addressLine2) formData.address_line2 = payload.addressLine2;
    if (payload.city) formData.city = payload.city;
    if (payload.province) formData.province = payload.province;
    if (payload.postalCode) formData.postal_code = payload.postalCode;
    if (payload.country) formData.country = payload.country;
    if (payload.dateOfBirth) formData.date_of_birth = payload.dateOfBirth;
    if (payload.businessName) formData.business_name = payload.businessName;
    if (payload.businessNumber) formData.business_number = payload.businessNumber;
    if (payload.clientCode) formData.client_code = payload.clientCode;
    if (payload.mailingName) formData.mailing_name = payload.mailingName;
    if (payload.salutation) formData.salutation = payload.salutation;
    if (payload.deliveryMethod) formData.delivery_method = payload.deliveryMethod;
    if (payload.contactType) formData.contact_type = payload.contactType;
    if (payload.leadSource) formData.lead_source = payload.leadSource;
    // Add custom fields
    if (customFields && typeof customFields === 'object') {
      for (const [key, val] of Object.entries(customFields as Record<string, unknown>)) {
        if (val !== undefined && val !== '') formData[key] = val;
      }
    }
    (payload as unknown as Record<string, unknown>).formData = formData;

    if (isEditing) {
      try {
        await updateMutation.mutateAsync({ ...payload, tagIds: selectedTagIds });
        toast({ variant: 'success', title: 'Contact updated successfully' });
        navigate(`/contacts/${id}`);
      } catch {
        toast({ variant: 'error', title: 'Failed to update contact' });
      }
      return;
    }

    // Create path — warn on duplicate email/phone before creating.
    try {
      const matches = await checkContactDuplicates({
        email: payload.email,
        phone: payload.phone,
      });
      if (matches.length > 0) {
        // Hold the payload and let the user decide via the warning modal.
        setPendingPayload(payload);
        setDuplicateMatches(matches);
        return;
      }
    } catch {
      // Duplicate check is advisory — if it fails, don't block creation.
    }

    await performCreate(payload);
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const entityTypeOptions = (entityTypes ?? [])
    .filter((et) => et.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((et) => ({ value: et.id, label: et.name }));

  // Build tabs: form definition tabs + Tags/Notes/Activity all in one row
  const allSections = formDefinition?.sections ?? [];
  const formTabSections = allSections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
  // If no tab sections exist, create a virtual "Details" tab for orphan sections
  const orphanSections = allSections.filter(
    (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
  );
  const hasOrphans = orphanSections.length > 0;

  const formTabs = [
    // Form builder tabs (or a default "Details" for orphan sections)
    ...(formTabSections.length > 0
      ? formTabSections.map((s) => ({ id: `section-${s.id}`, label: s.name }))
      : hasOrphans || formLoading
        ? [{ id: 'form-details', label: 'Details' }]
        : []),
    { id: 'tags', label: 'Tags' },
    ...(isEditing ? [{ id: 'notes', label: 'Notes' }] : []),
    ...(isEditing ? [{ id: 'activity', label: 'Activity' }] : []),
  ];

  // Auto-select first tab
  useEffect(() => {
    if (formLoading) return;
    if (formTabSections.length > 0) {
      setActiveTab(`section-${formTabSections[0].id}`);
    } else if (hasOrphans) {
      setActiveTab('form-details');
    } else {
      setActiveTab('tags');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefinition, formLoading, selectedEntityTypeId]);

  // ─── Loading state (MUST be after all hooks) ────────────────────────────────
  if (isEditing && contactLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
          {/* Header with entity type inline */}
          <div className="flex items-center justify-between border-b border-border pb-4 dark:border-border-dark">
            <div className="flex items-center gap-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {isEditing ? 'Edit Contact' : 'New Contact'}
                </h1>
              </div>
              <div className="w-52">
                <FormSelect
                  name="entityTypeId"
                  control={methods.control}
                  label=""
                  required
                  options={entityTypeOptions}
                  placeholder="Select entity type"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/contacts')}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          {selectedEntityTypeId && (
            <>
              <Tabs tabs={formTabs} activeTab={activeTab} onChange={setActiveTab} />

              {/* ─── Form Tab Panels (one per tab section) ─────────── */}
              {formTabSections.map((tab, tabIndex) => {
                // Child sections: those with parent_tab_id matching this tab
                const explicitChildren = allSections
                  .filter((s) => s.parent_tab_id === tab.id && s.render_as !== 'tab' && s.is_active)
                  .sort((a, b) => a.display_order - b.display_order);

                // Backward compat: if no sections have parent_tab_id set,
                // assign orphan sections to tabs by display_order proximity.
                // First tab gets all orphans that come before the next tab.
                let implicitChildren: typeof allSections = [];
                if (explicitChildren.length === 0) {
                  const nextTab = formTabSections[tabIndex + 1];
                  implicitChildren = orphanSections.filter((s) => {
                    const afterThisTab = s.display_order >= tab.display_order;
                    const beforeNextTab = nextTab ? s.display_order < nextTab.display_order : true;
                    return afterThisTab && beforeNextTab;
                  });
                }

                const children = explicitChildren.length > 0 ? explicitChildren : implicitChildren;
                const tabHasFields = tab.fields.filter((f) => f.is_active).length > 0;

                // Build sections to render: tab's own fields as a section + its children
                const sectionsToRender = [
                  ...(tabHasFields ? [{ ...tab, render_as: 'section' as const }] : []),
                  ...children,
                ];

                return (
                  <TabPanel key={tab.id} tabId={`section-${tab.id}`} activeTab={activeTab}>
                    {sectionsToRender.length > 0 ? (
                      <DynamicFormRenderer
                        formDefinition={{ ...formDefinition!, sections: sectionsToRender }}
                      />
                    ) : (
                      <Card>
                        <CardContent>
                          <p className="py-4 text-sm text-gray-500">
                            No fields configured for this tab.
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </TabPanel>
                );
              })}

              {/* ─── Orphan sections (no tabs defined — backward compat) ── */}
              {formTabSections.length === 0 && (
                <TabPanel tabId="form-details" activeTab={activeTab}>
                  {formLoading ? (
                    <Card>
                      <CardContent>
                        <div className="flex items-center justify-center py-12">
                          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                            <span className="text-sm">Loading form...</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : formDefinition ? (
                    <DynamicFormRenderer
                      formDefinition={{ ...formDefinition, sections: orphanSections }}
                    />
                  ) : (
                    <Card>
                      <CardContent>
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <svg
                            className="h-12 w-12 text-amber-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                            />
                          </svg>
                          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            No form attached to this entity type
                          </p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Please configure a form in Settings → Form Builder and map it to this
                            entity type.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabPanel>
              )}

              {/* ─── Tags Tab ────────────────────────────────── */}
              <TabPanel tabId="tags" activeTab={activeTab}>
                <Card>
                  <CardHeader title="Tags" />
                  <CardContent>
                    {availableTags && availableTags.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map((tag) => (
                          <TagChip
                            key={tag.id}
                            tag={tag}
                            selected={selectedTagIds.includes(tag.id)}
                            onToggle={() => handleTagToggle(tag.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No tags available.</p>
                    )}
                  </CardContent>
                </Card>
              </TabPanel>

              {/* ─── Notes Tab (edit mode only) ───────────────── */}
              {isEditing && (
                <TabPanel tabId="notes" activeTab={activeTab}>
                  <NotesTab contactId={id!} />
                </TabPanel>
              )}

              {/* ─── Activity Tab (edit mode only) ────────────── */}
              {isEditing && (
                <TabPanel tabId="activity" activeTab={activeTab}>
                  <ActivityTab contactId={id!} />
                </TabPanel>
              )}
            </>
          )}
        </form>
      </FormProvider>

      <DuplicateWarningModal
        open={duplicateMatches.length > 0}
        duplicates={duplicateMatches}
        isSaving={createMutation.isPending}
        onConfirm={handleConfirmDuplicate}
        onCancel={handleCancelDuplicate}
      />
    </div>
  );
}

// ─── Tag Chip ─────────────────────────────────────────────────────────────────

function TagChip({
  tag,
  selected,
  onToggle,
}: {
  tag: Tag;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
          selected
            ? 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-900/30 dark:text-brand-300'
            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
        }`}
    >
      {tag.color && (
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tag.color }} />
      )}
      {tag.name}
      {selected && (
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </button>
  );
}

// ─── Notes Tab ────────────────────────────────────────────────────────────────

function NotesTab({ contactId }: { contactId: string }) {
  const { toast } = useToast();
  const { data: notes, isLoading } = useContactNotes(contactId);
  const { data: categories } = useNoteCategories();
  const createNoteMutation = useCreateNote(contactId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteCategory, setNoteCategory] = useState('');
  const [reminderDate, setReminderDate] = useState('');

  // Check if selected category is a scheduling category
  const selectedCategoryObj = categories?.find(
    (cat) => cat.name.toLowerCase().replace(/\s+/g, '_') === noteCategory,
  );
  const isSchedulingCategory = selectedCategoryObj?.isScheduling ?? false;

  const handleAddNote = useCallback(async () => {
    if (!noteContent.trim()) return;
    if (isSchedulingCategory && !reminderDate) {
      toast({ variant: 'error', title: 'Reminder date is required for this category' });
      return;
    }
    try {
      await createNoteMutation.mutateAsync({
        content: noteContent.trim(),
        category: noteCategory || 'general',
        reminderDate: isSchedulingCategory && reminderDate ? reminderDate : undefined,
      });
      setNoteContent('');
      setNoteCategory('');
      setReminderDate('');
      setShowAddForm(false);
      toast({ variant: 'success', title: 'Note added' });
    } catch {
      toast({ variant: 'error', title: 'Failed to add note' });
    }
  }, [noteContent, noteCategory, reminderDate, isSchedulingCategory, createNoteMutation, toast]);

  return (
    <Card>
      <CardHeader
        title="Notes"
        action={
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : 'Add Note'}
          </Button>
        }
      />
      <CardContent>
        {showAddForm && (
          <div className="mb-4 rounded-lg border border-border p-4 dark:border-border-dark space-y-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Category
              </label>
              <select
                value={noteCategory}
                onChange={(e) => setNoteCategory(e.target.value)}
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Select category</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.name.toLowerCase().replace(/\s+/g, '_')}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={4}
                maxLength={10000}
                placeholder="Type your note here..."
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            {isSchedulingCategory && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reminder Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={reminderDate}
                  onChange={(e) => setReminderDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500">
                  A reminder will be created and triggered on this date.
                </p>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                onClick={handleAddNote}
                loading={createNoteMutation.isPending}
                disabled={!noteContent.trim()}
              >
                Save Note
              </Button>
            </div>
          </div>
        )}
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading notes...</p>
        ) : notes && notes.length > 0 ? (
          <ul role="list" className="divide-y divide-border dark:divide-border-dark">
            {notes.map((note) => (
              <li key={note.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="default" size="sm">
                        {note.category}
                      </Badge>
                      {note.createdByName && (
                        <span className="text-xs text-gray-500">by {note.createdByName}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                      {note.content}
                    </p>
                  </div>
                  <time className="shrink-0 text-xs text-gray-400">
                    {new Date(note.createdAt).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </time>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">
            No notes yet. Add a note to keep track of interactions.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Activity Tab ─────────────────────────────────────────────────────────────

function ActivityTab({ contactId }: { contactId: string }) {
  const { data: activityData, isLoading } = useContactActivity(contactId);

  return (
    <Card>
      <CardHeader title="Activity Timeline" />
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading activity...</p>
        ) : activityData && activityData.length > 0 ? (
          <ul role="list" className="space-y-4">
            {activityData.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3">
                <div className="mt-1 h-2 w-2 rounded-full bg-brand-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-gray-100">{entry.description}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {entry.userName && (
                      <span className="text-xs text-gray-500">{entry.userName}</span>
                    )}
                    <time className="text-xs text-gray-400">
                      {new Date(entry.createdAt).toLocaleDateString('en-CA', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </time>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No activity recorded yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
